/**
 * CombinedOptimizationTab - 2D Sensitivity Analysis for Zinstopf Size + Withdrawal Rate
 * 
 * This component performs comprehensive 2D optimization by testing all combinations
 * of Zinstopf sizes (anzahlJahreTopf2) and withdrawal rates (portfolioEntnahmeRate)
 * to find the optimal configuration for portfolio sustainability.
 * 
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Icon,
  Input,
  Popover,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { CombinedOptimizationConfig, CombinedOptimizationResult } from '@/types/simulation';
import { CombinedMonteCarloEngine } from '@/services/CombinedMonteCarloEngine';
import { SteuerParameter } from '@/types';
import { SIMULATION_CONFIG } from '@/config/simulation';

interface CombinedOptimizationTabProps {
  steuerParameter: SteuerParameter | null;
  monteCarloEngine: CombinedMonteCarloEngine | undefined;
  iterations: number;
}

export const CombinedOptimizationTab: React.FC<CombinedOptimizationTabProps> = ({
  steuerParameter,
  monteCarloEngine,
  iterations,
}) => {
  // State management
  const [config, setConfig] = useState<CombinedOptimizationConfig>({
    minZinstopfYears: 1,
    maxZinstopfYears: 10,
    zinstopfStepSize: 1,
    minWithdrawalRate: 2.0,
    maxWithdrawalRate: 6.0,
    withdrawalStepSize: 0.25,
  });
  const [results, setResults] = useState<CombinedOptimizationResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentZinstopf, setCurrentZinstopf] = useState<number | undefined>();
  const [currentRate, setCurrentRate] = useState<number | undefined>();
  const [totalConfigs, setTotalConfigs] = useState<number | undefined>();

  // Calculate total configurations
  const calculatedTotalConfigs = useMemo(() => {
    const zCount = Math.floor((config.maxZinstopfYears - config.minZinstopfYears) / config.zinstopfStepSize) + 1;
    const rCount = Math.floor((config.maxWithdrawalRate - config.minWithdrawalRate) / config.withdrawalStepSize) + 1;
    return zCount * rCount;
  }, [config]);

  // Run the optimization analysis
  const handleRunOptimization = async () => {
    if (!monteCarloEngine || !steuerParameter) {
      return;
    }

    setIsRunning(true);
    setProgress(0);
    setResults([]);

    try {
      const optimizationResults = await monteCarloEngine.runCombinedOptimization(
        iterations,
        config,
        (prog, zinstopf, rate, total) => {
          setProgress(Math.round(prog));
          setCurrentZinstopf(zinstopf);
          setCurrentRate(rate);
          setTotalConfigs(total);
        }
      );

      setResults(optimizationResults);
    } catch (error) {
      console.error('Optimization failed:', error);
    } finally {
      setIsRunning(false);
      setProgress(0);
      setCurrentZinstopf(undefined);
      setCurrentRate(undefined);
      setTotalConfigs(undefined);
    }
  };

  // Optimale Konfiguration: höchste Entnahme-Rate mit Erfolgsquote >= Schwelle
  // (Argmax über die Erfolgsquote degenerierte zur niedrigsten Testrate, weil die
  // Erfolgsquote monoton in der Entnahmerate fällt - bei 100%-Gleichstand gewann
  // immer die erste = niedrigste Rate.) Tie-Break bei gleicher Rate: höhere
  // Erfolgsquote (wählt die beste Zinstopf-Größe für diese Rate).
  // Fallback ohne Schwellen-Treffer: höchste Erfolgsquote (altes Verhalten).
  const optimalConfig = useMemo(() => {
    if (results.length === 0) return null;

    const threshold = SIMULATION_CONFIG.OPTIMAL_WITHDRAWAL_SUCCESS_THRESHOLD * 100;
    const meetsThreshold = results.filter(
      r => r.statistics.threeTopfSuccessRate >= threshold
    );

    if (meetsThreshold.length > 0) {
      return meetsThreshold.reduce((best, current) => {
        if (current.withdrawalRate !== best.withdrawalRate) {
          return current.withdrawalRate > best.withdrawalRate ? current : best;
        }
        return current.statistics.threeTopfSuccessRate > best.statistics.threeTopfSuccessRate
          ? current
          : best;
      });
    }

    return results.reduce((best, current) =>
      current.statistics.threeTopfSuccessRate > best.statistics.threeTopfSuccessRate
        ? current
        : best
    );
  }, [results]);

  // Validation
  const isConfigValid = 
    config.minZinstopfYears < config.maxZinstopfYears &&
    config.minWithdrawalRate < config.maxWithdrawalRate;

  const estimatedMinutes = Math.round((calculatedTotalConfigs * iterations) / 3000); // Rough estimate

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Configuration Section */}
      <Container
        header={
          <Header 
            variant="h2"
            description="Konfigurieren Sie die Bereiche für Zinstopf-Größe und Entnahme-Rate"
          >
            Kombinierte Optimierung Konfiguration
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <ColumnLayout columns={2}>
            <SpaceBetween direction="vertical" size="s">
              <Box variant="h3">Zinstopf-Größe (Jahre)</Box>
              <FormField label="Minimum Jahre">
                <Input
                  type="number"
                  value={config.minZinstopfYears.toString()}
                  onChange={({ detail }) => {
                    const value = parseInt(detail.value) || 1;
                    setConfig({ ...config, minZinstopfYears: Math.max(1, Math.min(value, 30)) });
                  }}
                  disabled={isRunning}
                />
              </FormField>
              <FormField label="Maximum Jahre">
                <Input
                  type="number"
                  value={config.maxZinstopfYears.toString()}
                  onChange={({ detail }) => {
                    const value = parseInt(detail.value) || 10;
                    setConfig({ ...config, maxZinstopfYears: Math.max(1, Math.min(value, 30)) });
                  }}
                  disabled={isRunning}
                />
              </FormField>
              <Box>
                <Box variant="awsui-key-label">Schrittweite</Box>
                <Box variant="p">1 Jahr (fest)</Box>
              </Box>
            </SpaceBetween>

            <SpaceBetween direction="vertical" size="s">
              <Box variant="h3">Entnahme-Rate (%)</Box>
              <FormField label="Minimale Rate (%)">
                <Input
                  type="number"
                  value={config.minWithdrawalRate.toString()}
                  onChange={({ detail }) => {
                    const value = parseFloat(detail.value) || 0;
                    setConfig({ ...config, minWithdrawalRate: Math.max(0.1, Math.min(value, 20)) });
                  }}
                  step={0.25}
                  disabled={isRunning}
                />
              </FormField>
              <FormField label="Maximale Rate (%)">
                <Input
                  type="number"
                  value={config.maxWithdrawalRate.toString()}
                  onChange={({ detail }) => {
                    const value = parseFloat(detail.value) || 0;
                    setConfig({ ...config, maxWithdrawalRate: Math.max(0.1, Math.min(value, 20)) });
                  }}
                  step={0.25}
                  disabled={isRunning}
                />
              </FormField>
              <Box>
                <Box variant="awsui-key-label">Schrittweite</Box>
                <Box variant="p">0.25% (fest)</Box>
              </Box>
            </SpaceBetween>
          </ColumnLayout>

          <Box>
            <Box variant="awsui-key-label">Gesamtanzahl Konfigurationen</Box>
            <Box variant="p">
              {calculatedTotalConfigs} Kombinationen ({calculatedTotalConfigs} × {iterations} = {calculatedTotalConfigs * iterations} Simulationen)
            </Box>
            <Box variant="small" color="text-body-secondary">
              Geschätzte Dauer: ~{estimatedMinutes}-{estimatedMinutes * 2} Minuten
            </Box>
          </Box>

          {calculatedTotalConfigs > 200 && (
            <Alert type="warning">
              <strong>Warnung:</strong> Diese Konfiguration erzeugt {calculatedTotalConfigs} Kombinationen. 
              Die Ausführung kann sehr lange dauern (~{estimatedMinutes}-{estimatedMinutes * 2} Minuten). 
              Erwägen Sie, die Bereiche zu reduzieren.
            </Alert>
          )}

          <Box>
            <Button
              variant="primary"
              onClick={handleRunOptimization}
              disabled={isRunning || !monteCarloEngine || !steuerParameter || !isConfigValid}
              loading={isRunning}
            >
              {isRunning ? 'Optimierung läuft...' : `Optimierung starten (${calculatedTotalConfigs} Konfigurationen)`}
            </Button>
            {!isConfigValid && (
              <Box color="text-status-error" fontSize="body-s" margin={{ top: 'xs' }}>
                Minimum-Werte müssen kleiner als Maximum-Werte sein
              </Box>
            )}
          </Box>
        </SpaceBetween>
      </Container>

      <Container>
        <SpaceBetween direction="vertical" size="m">
          {/* Progress Bar */}
          {isRunning && (
            <Box>
              <Box variant="h4">Optimierung läuft...</Box>
              {currentZinstopf !== undefined && currentRate !== undefined && totalConfigs !== undefined && (
                <Box variant="small" color="text-body-secondary" margin={{ bottom: 'xs' }}>
                  Zinstopf: {currentZinstopf}J, Entnahme: {currentRate.toFixed(2)}% | Fortschritt: {progress}%
                </Box>
              )}
              <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: '#0073bb',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </Box>
          )}

          {/* Optimal Configuration Alert */}
          {optimalConfig && !isRunning && (
            <Alert
              type="success"
              header={`Optimale Konfiguration: ${optimalConfig.zinstopfYears}J Zinstopf + ${optimalConfig.withdrawalRate.toFixed(2)}% Entnahme`}
            >
              <SpaceBetween direction="vertical" size="xs">
                <Box>
                  Die Kombination von <strong>{optimalConfig.zinstopfYears} Jahren Zinstopf</strong> und{' '}
                  <strong>{optimalConfig.withdrawalRate.toFixed(2)}% Entnahme-Rate</strong> ist die höchste
                  getestete Entnahme-Rate mit einer 3-Topf-Erfolgsquote von mindestens{' '}
                  {(SIMULATION_CONFIG.OPTIMAL_WITHDRAWAL_SUCCESS_THRESHOLD * 100).toFixed(0)}%
                  (tatsächlich: <strong>{optimalConfig.statistics.threeTopfSuccessRate.toFixed(1)}%</strong>).
                </Box>
                {steuerParameter && (
                  <Box>
                    Jährliche Entnahme: <strong>
                      {((steuerParameter.vorhandenesKapital * optimalConfig.withdrawalRate) / 100).toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      })}
                    </strong>
                  </Box>
                )}
              </SpaceBetween>
            </Alert>
          )}

          {/* Comprehensive Table */}
          {results.length > 0 && steuerParameter && (
            <Container
              header={
                <Header variant="h3">
                  Detaillierte Ergebnisse aller Kombinationen
                </Header>
              }
            >
              <Table
                columnDefinitions={[
                  {
                    id: 'zinstopf',
                    header: 'Zinstopf',
                    cell: (item) => (
                      <Box>
                        {item.zinstopfYears === optimalConfig?.zinstopfYears && 
                         item.withdrawalRate === optimalConfig?.withdrawalRate && '★ '}
                        {item.zinstopfYears}J
                      </Box>
                    ),
                  },
                  {
                    id: 'rate',
                    header: 'Entnahme-Rate',
                    cell: (item) => `${item.withdrawalRate.toFixed(2)}%`,
                  },
                  {
                    id: 'annualWithdrawal',
                    header: 'Jährliche Entnahme',
                    cell: (item) => (
                      ((steuerParameter.vorhandenesKapital * item.withdrawalRate) / 100).toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      })
                    ),
                  },
                  {
                    id: 'threeTopfSuccess',
                    header: '3-Topf Erfolgsquote',
                    cell: (item) => `${item.statistics.threeTopfSuccessRate.toFixed(1)}%`,
                  },
                  {
                    id: 'oneTopfSuccess',
                    header: '1-Topf Erfolgsquote',
                    cell: (item) => `${item.statistics.oneTopfSuccessRate.toFixed(1)}%`,
                  },
                  {
                    id: 'threeTopfBalance',
                    header: 'Ø Endsaldo 3-Topf',
                    cell: (item) =>
                      item.statistics.threeTopfAvgFinalBalance.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      }),
                  },
                  {
                    id: 'threeTopfMedianBalance',
                    header: 'Median Endsaldo 3-Topf',
                    cell: (item) =>
                      item.statistics.threeTopfMedianFinalBalance.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      }),
                  },
                  {
                    id: 'oneTopfBalance',
                    header: 'Ø Endsaldo 1-Topf',
                    cell: (item) =>
                      item.statistics.oneTopfAvgFinalBalance.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      }),
                  },
                  {
                    id: 'oneTopfMedianBalance',
                    header: 'Median Endsaldo 1-Topf',
                    cell: (item) =>
                      item.statistics.oneTopfMedianFinalBalance.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      }),
                  },
                  {
                    id: 'advantage',
                    header: (
                      <SpaceBetween direction="horizontal" size="xs">
                        <span>Vorteil 3-Topf</span>
                        <Popover
                          size="large"
                          position="top"
                          triggerType="custom"
                          dismissButton={false}
                          content={
                            <Box variant="p">
                              Prozentsatz der Monte-Carlo-Iterationen, in denen die 3-Topf-Strategie einen höheren Endsaldo als die 1-Topf-Strategie erreichte.
                              <br /><br />
                              <strong>Interpretation:</strong><br />
                              • 50% = Beide Strategien gleich stark<br />
                              • &gt;50% = 3-Topf überwiegend besser<br />
                              • &lt;50% = 1-Topf überwiegend besser<br />
                              • ≥70% = 3-Topf klar dominant
                            </Box>
                          }
                        >
                          <Icon name="status-info" size="small" />
                        </Popover>
                      </SpaceBetween>
                    ),
                    cell: (item) => `${item.statistics.threeTopfAdvantageRate.toFixed(1)}%`,
                  },
                ]}
                items={results}
                sortingDisabled={true}
                empty={
                  <Box textAlign="center" color="text-body-secondary">
                    <p>Keine Daten verfügbar</p>
                  </Box>
                }
              />
            </Container>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
